import React, { useState, useEffect, useRef } from "react";
import { Shield, ChevronRight, Play } from "lucide-react";
import mahalaxmi_temple from "../../../assets/Home/mahalaxmin-temple.jpg";
import mahalaxmi from "../../../assets/Home/mahalaxmi.png";
import mahalaxmi_temple2 from "../../../assets/Home/mahalaxmin-temple-02.jpg";
import mahalaxmi2 from "../../../assets/Home/mahalaxmi_2.jpg";
import mahalaxmi3 from "../../../assets/Home/mahalaxmi_3.jpg";
import { useNavigate } from "react-router-dom";
import {
  WhatsAppCommunityCard,
  WhatsAppCommunityPill,
} from "../WhatsappCommunity";

const HeroSection = () => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const navigate = useNavigate();
  const intervalRef = useRef(null);
  const pauseTimeoutRef = useRef(null);

  const images = [
    {
      id: 0,
      title: "Shree Mahalaxmi Ambabai",
      subtitle: "Main Deity",
      src: mahalaxmi2,
      alt: "Shree Mahalaxmi Ambabai Goddess",
    },
    {
      id: 1,
      title: "Temple Exterior",
      subtitle: "Sacred Architecture",
      src: mahalaxmi_temple,
      alt: "Mahalaxmi Temple Exterior",
    },
    {
      id: 2,
      title: "Shree Mahalaxmi Ambabai",
      subtitle: "Devi",
      src: mahalaxmi,
      alt: "Shree Mahalaxmi Ambabai Goddess",
    },
    {
      id: 3,
      title: "Temple Surroundings",
      subtitle: "Sacred Premises",
      src: mahalaxmi_temple2,
      alt: "Temple Surroundings and Premises",
    },
    {
      id: 4,
      title: "Mahalaxmi Ambabai",
      subtitle: "Traditional Puja",
      src: mahalaxmi3,
      alt: "Traditional Puja",
    },
  ];

  // Preload images
  useEffect(() => {
    const loadImages = async () => {
      const imagePromises = images.map((img) => {
        return new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = resolve;
          image.onerror = reject;
          image.src = img.src;
        });
      });

      try {
        await Promise.all(imagePromises);
        setImagesLoaded(true);
      } catch (error) {
        console.error("Error loading images:", error);
        setImagesLoaded(true); // Continue anyway
      }
    };

    loadImages();
  }, []);

  // Clean up intervals and timeouts
  const clearAllTimers = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
  };

  // Auto-carousel effect
  useEffect(() => {
    if (!imagesLoaded) return;

    clearAllTimers();

    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setSelectedImage((prev) => (prev + 1) % images.length);
      }, 3500);
    }

    return clearAllTimers;
  }, [isPaused, imagesLoaded, images.length]);

  // Handle manual image selection
  const handleImageSelect = (index) => {
    setSelectedImage(index);
    setIsPaused(true);

    // Clear any existing pause timeout
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }

    // Resume auto-rotation after 5 seconds
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 5000);
  };

  // Live Stats Component
  const LiveStats = () => (
    <div className="flex items-center space-x-8 pt-4">
      <div className="text-center transform hover:scale-105 transition-all">
        <div className="text-3xl font-bold text-gray-800 flex items-center">
          <span>1470</span>
          <span className="text-orange-500 text-lg ml-1">AD</span>
        </div>
        <div className="text-sm text-gray-600">Kolhapur lineage since</div>
      </div>
      <div className="text-center transform hover:scale-105 transition-all">
        <div className="text-3xl font-bold text-gray-800 flex items-center">
          <span>54th</span>
          <span className="text-green-500 text-lg ml-1">Gen</span>
        </div>
        <div className="text-sm text-gray-600">Hereditary Shreepujak</div>
      </div>
      <div className="text-center transform hover:scale-105 transition-all">
        <div className="text-3xl font-bold text-gray-800 flex items-center">
          <span>1000+</span>
          <span className="text-purple-500 text-lg ml-1">Years</span>
        </div>
        <div className="text-sm text-gray-600">Mahalaxmi Ambabai Service</div>
      </div>
    </div>
  );

  // Authenticity Badge Component
  const AuthenticityBadge = () => (
    <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4">
      <div className="flex items-center space-x-3">
        <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-xl">
          🛕
        </div>
        <div>
          <div className="font-bold text-gray-800">
            Born & Raised in Kolhapur Sacred Tradition
          </div>
          <div className="text-sm text-gray-600">
            Direct descendant of Mahalaxmi Ambabai's Shreepujak family
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <section className="relative py-16 px-4 bg-gradient-to-r from-orange-100 via-white to-red-100 overflow-hidden">
      {/* Floating Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 text-6xl opacity-10 animate-bounce">
          🕉️
        </div>
        <div className="absolute top-40 right-20 text-4xl opacity-10 animate-pulse">
          🪔
        </div>
        <div
          className="absolute bottom-20 left-1/4 text-5xl opacity-10 animate-bounce"
          style={{ animationDelay: "1000ms" }}
        >
          🙏
        </div>
      </div>

      <div className="container mx-auto max-w-7xl relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="flex items-center space-x-2 text-orange-600 font-medium">
              <span>Hereditary Shreepujak Of Mahalaxmi Ambabai Kolhapur</span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold text-gray-800 leading-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
                Kolhapur Mahalaxmi Ambabai
              </span>
              <br />
              Puja by Hereditary Shreepujak 🙏
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed">
              🛕 Traditional Kolhapur rituals
              <span className="hidden md:inline"> • </span>
              <br className="md:hidden" />
              📿 54th generation Shreepujak
              <span className="hidden md:inline"> • </span>
              <br className="md:hidden" />
              🎁 Blessed prasad from authentic ingredients
            </p>
            <div className="pt-4 space-y-4">
              <button
                onClick={() => navigate("/services")}
                className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-5 rounded-full text-lg lg:text-xl font-semibold hover:from-orange-600 hover:to-red-600 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl flex items-center justify-center group min-w-[280px] lg:min-w-[350px]"
              >
                Book Mahalaxmi Ambabai Puja Of Kolhapur
                <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="lg:hidden">
                <WhatsAppCommunityPill />
              </div>
            </div>
            {/* Desktop: Show Live Stats and Authenticity Badge */}
            <div className="hidden lg:block space-y-6">
              <LiveStats />
              <AuthenticityBadge />
            </div>
          </div>

          {/* Right Side - Interactive Gallery with Images */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:scale-105 transition-all duration-300 border border-orange-100">
              {/* Main image area - Auto-rotating carousel */}
              <div
                className="relative h-[400px] overflow-hidden cursor-pointer"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                {imagesLoaded ? (
                  <img
                    src={images[selectedImage].src}
                    alt={images[selectedImage].alt}
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out transform"
                    style={{
                      opacity: 1,
                      transform: "scale(1)",
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 w-full h-full bg-gray-200 flex items-center justify-center">
                    <div className="text-gray-500">Loading images...</div>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/15"></div>
              </div>

              {/* Navigation dots and info */}
              <div className="p-3 bg-gray-50">
                {/* Image title */}
                <div className="text-center mb-2">
                  <div className="text-base font-semibold text-gray-800">
                    {images[selectedImage].title}
                  </div>
                  <div className="text-xs text-gray-600">
                    {images[selectedImage].subtitle}
                  </div>
                </div>

                {/* Navigation dots */}
                <div className="flex space-x-2 justify-center">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => handleImageSelect(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                        selectedImage === index
                          ? "bg-orange-500 scale-125"
                          : "bg-gray-300 hover:bg-gray-400"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="hidden lg:block mt-6">
              <WhatsAppCommunityCard />
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-8 h-8 bg-orange-300 rounded-full opacity-50"></div>
            <div className="absolute top-1/2 -right-6 w-6 h-6 bg-red-300 rounded-full opacity-40"></div>
          </div>
        </div>

        {/* Mobile: Show Live Stats and Authenticity Badge below image */}
        <div className="lg:hidden mt-8 space-y-6">
          <LiveStats />
          <AuthenticityBadge />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
